import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext';
import ChatMessage from './ChatMessage';
import ModelSelector from './ModelSelector';
import ImageModelSelector from './ImageModelSelector';
import FileViewerModal from './FileViewerModal';
import { useToast } from '../contexts/ToastContext';
import * as pdfjsLib from 'pdfjs-dist';
import PdfWorker from 'pdfjs-dist/build/pdf.worker.mjs?worker';
import ChatInputArea from './ChatInputArea';
import LiveModeUI from './LiveModeUI';
import useMessageSender from '../hooks/useMessageSender';
import { listImageModelsApi } from '../services/imageService';
import { DEFAULT_CHAT_MODEL_ID, DEEP_RESEARCH_MODEL_ID, getPreferredModelId } from '../config/modelConfig';

const ProjectBadge = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px 3px 8px;
  border-radius: 999px;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.sidebar};
  color: ${props => props.theme.text};
  font-size: 0.75rem;
  opacity: 0.75;
  text-decoration: none;
  transition: opacity 0.15s;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover { opacity: 1; }

  svg { width: 12px; height: 12px; flex-shrink: 0; }
`;
import {
  ChatWindowContainer,
  ChatHeader,
  ChatTitleSection,
  ChatTitle,
  ModelSelectorsRow,
  MessageList,
  EmptyState,
} from './ChatWindow.styled';

pdfjsLib.GlobalWorkerOptions.workerPort = new PdfWorker();

const ChatWindow = forwardRef(({
  chat,
  addMessage,
  updateMessage,
  updateChatTitle,
  selectedModel: initialSelectedModel,
  settings,
  $sidebarCollapsed = false,
  availableModels,
  onAttachmentChange,
  onModelChange,
  showGreeting = true,
  isWhiteboardOpen,
  onToggleWhiteboard,
  onCloseWhiteboard,
  isEquationEditorOpen,
  onToggleEquationEditor,
  onCloseEquationEditor,
  isGraphingOpen,
  onToggleGraphing,
  onCloseGraphing,
  isFlowchartOpen,
  onToggleFlowchart,
  onCloseFlowchart,
  isSandbox3DOpen,
  onToggleSandbox3D,
  onCloseSandbox3D,
  onToolbarToggle,
  onUserTyping,
  focusModeActive = false,
  onMessageSent,
  projects = [],
  pendingMessage = null,
  onPendingMessageConsumed,
  onFileViewerOpenChange,
}, ref) => {
  // All hooks at the top level - no conditional returns before this
  const [selectedModel, setSelectedModel] = useState(initialSelectedModel || DEFAULT_CHAT_MODEL_ID);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [isLiveModeOpen, setIsLiveModeOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(chat?.title || 'New Conversation');
  const [uploadedFileData, setUploadedFileData] = useState(null);
  const [resetFileUpload, setResetFileUpload] = useState(false);
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [fileToView, setFileToView] = useState(null);
  const [animateDown, setAnimateDown] = useState(false);
  const [profilePicture, setProfilePicture] = useState(() => {
    try {
      return localStorage.getItem('profilePicture') || null;
    } catch (err) {
      console.error('Failed to read profile picture from localStorage', err);
      return null;
    }
  });

  // Image generation model state
  const [isImagePromptMode, setIsImagePromptMode] = useState(false);
  const [availableImageModels, setAvailableImageModels] = useState([]);
  const [selectedImageModel, setSelectedImageModel] = useState(null);
  const [activeActionChip, setActiveActionChip] = useState(null);

  const messagesEndRef = useRef(null);
  const chatInputAreaRef = useRef(null);
  const prevIsEmptyRef = useRef(false);
  const previousNonDeepResearchModelRef = useRef(initialSelectedModel || DEFAULT_CHAT_MODEL_ID);
  const deepResearchChipLockRef = useRef(false);

  const { t } = useTranslation();
  const toast = useToast();
  const theme = useTheme();
  const showProfilePicture = settings?.showProfilePicture !== false;
  const newConversationLabel = t('chat.newConversation', 'New Conversation');

  // Memoized values
  const chatIsEmpty = useMemo(() => {
    return !chat || !chat.messages || chat.messages.length === 0;
  }, [chat]);

  const shouldStartAnimationThisRender = useMemo(() => {
    return prevIsEmptyRef.current && !chatIsEmpty;
  }, [chatIsEmpty]);

  const effectiveAnimateDownSignal = useMemo(() => {
    return animateDown || shouldStartAnimationThisRender;
  }, [animateDown, shouldStartAnimationThisRender]);

  const showEmptyStateStatic = useMemo(() => {
    return showGreeting && chatIsEmpty && !effectiveAnimateDownSignal;
  }, [showGreeting, chatIsEmpty, effectiveAnimateDownSignal]);

  const animateEmptyStateOut = useMemo(() => {
    return (!chatIsEmpty || shouldStartAnimationThisRender || !showGreeting) && effectiveAnimateDownSignal;
  }, [chatIsEmpty, shouldStartAnimationThisRender, showGreeting, effectiveAnimateDownSignal]);

  useEffect(() => {
    const handleProfilePictureChange = (event) => {
      setProfilePicture(event?.detail?.profilePicture || null);
    };

    window.addEventListener('profilePictureChanged', handleProfilePictureChange);
    return () => {
      window.removeEventListener('profilePictureChanged', handleProfilePictureChange);
    };
  }, []);

  // Callbacks
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleFileSelected = useCallback(async (files) => {
    if (!files) {
      setUploadedFileData(null);
      if (onAttachmentChange) onAttachmentChange(false);
      return;
    }

    // Handle single file (backward compatibility)
    if (!Array.isArray(files)) {
      files = [files];
    }

    if (files.length === 0) {
      setUploadedFileData(null);
      if (onAttachmentChange) onAttachmentChange(false);
      return;
    }

    // Get existing files to add to (but don't exceed 4 total)
    const existingFiles = uploadedFileData ? (Array.isArray(uploadedFileData) ? uploadedFileData : [uploadedFileData]) : [];
    const totalFiles = existingFiles.length + files.length;

    if (totalFiles > 4) {
      alert(`You can only upload up to 4 files total. You currently have ${existingFiles.length} files and are trying to add ${files.length} more.`);
      return;
    }

    setIsProcessingFile(true);
    if (onAttachmentChange) onAttachmentChange(true);

    try {
      const processedFiles = [...existingFiles]; // Start with existing files

      for (const file of files) {
        const isImage = file.type.startsWith('image/');
        const isText = file.type === 'text/plain' || file.isPastedText;
        const isPdf = file.type === 'application/pdf';

        // Check for code files by extension
        const codeExtensions = [
          'js', 'jsx', 'ts', 'tsx', 'mjs', 'cjs',  // JavaScript/TypeScript
          'py', 'pyw', 'pyi',                       // Python
          'java', 'kt', 'kts', 'scala',             // JVM languages
          'c', 'cpp', 'cc', 'cxx', 'h', 'hpp', 'hxx', // C/C++
          'cs',                                      // C#
          'go',                                      // Go
          'rs',                                      // Rust
          'rb', 'erb',                               // Ruby
          'php', 'phtml',                            // PHP
          'swift',                                   // Swift
          'sh', 'bash', 'zsh', 'fish',              // Shell scripts
          'ps1', 'psm1', 'psd1',                    // PowerShell
          'sql',                                     // SQL
          'r', 'R',                                  // R
          'lua',                                     // Lua
          'pl', 'pm',                                // Perl
          'ex', 'exs',                               // Elixir
          'erl', 'hrl',                              // Erlang
          'hs', 'lhs',                               // Haskell
          'clj', 'cljs', 'cljc', 'edn',             // Clojure
          'ml', 'mli',                               // OCaml
          'fs', 'fsi', 'fsx',                        // F#
          'dart',                                    // Dart
          'vue', 'svelte',                           // Frontend frameworks
          'html', 'htm', 'xml', 'xhtml',            // Markup
          'css', 'scss', 'sass', 'less', 'styl',   // Stylesheets
          'json', 'yaml', 'yml', 'toml',            // Config/data
          'md', 'markdown', 'rst', 'txt',           // Documentation
          'dockerfile', 'makefile', 'cmake',        // Build files
          'ini', 'cfg', 'conf', 'config',           // Config files
          'env', 'gitignore', 'editorconfig'        // Dotfiles
        ];
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
        const isCodeFile = codeExtensions.includes(fileExtension) ||
          file.name.toLowerCase() === 'dockerfile' ||
          file.name.toLowerCase() === 'makefile' ||
          file.name.toLowerCase().startsWith('.env');

        if (!isImage && !isText && !isPdf && !isCodeFile) {
          alert(`Unsupported file type: ${file.name}`);
          continue;
        }

        if (file.size > 10 * 1024 * 1024) {
          alert(`File too large: ${file.name}. Max size is 10MB.`);
          continue;
        }

        if (isImage) {
          const reader = new FileReader();
          const dataUrl = await new Promise((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          processedFiles.push({
            type: 'image',
            content: dataUrl,
            dataUrl: dataUrl,
            name: file.name,
            originalFile: file
          });
        } else if (isText) {
          if (file.isPastedText) {
            // Handle pasted text
            processedFiles.push({
              type: 'text',
              content: file.pastedContent,
              text: file.pastedContent,
              name: file.name,
              isPastedText: true
            });
          } else {
            // Handle regular text file
            const reader = new FileReader();
            const text = await new Promise((resolve, reject) => {
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsText(file);
            });
            processedFiles.push({
              type: 'text',
              content: text,
              text: text,
              name: file.name
            });
          }
        } else if (isPdf) {
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          let fullText = '';
          let pdfThumbnail = null;

          // Generate thumbnail from first page
          try {
            const firstPage = await pdf.getPage(1);
            const viewport = firstPage.getViewport({ scale: 0.5 });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            // Scale to fit within 80x80 while maintaining aspect ratio
            const maxSize = 80;
            const scale = Math.min(maxSize / viewport.width, maxSize / viewport.height);
            canvas.width = viewport.width * scale;
            canvas.height = viewport.height * scale;

            const scaledViewport = firstPage.getViewport({ scale: 0.5 * scale });

            await firstPage.render({
              canvasContext: context,
              viewport: scaledViewport
            }).promise;

            pdfThumbnail = canvas.toDataURL('image/png');
          } catch (thumbnailError) {
            console.warn('Could not generate PDF thumbnail:', thumbnailError);
          }

          // Extract text from all pages
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            fullText += textContent.items.map(item => item.str).join(' ') + '\n';
          }
          if (!fullText.trim()) {
            fullText = "This appears to be a scanned PDF without extractable text.";
          }
          const trimmedText = fullText.trim();
          processedFiles.push({
            type: 'pdf',
            content: trimmedText,
            text: trimmedText,
            name: file.name,
            pdfThumbnail: pdfThumbnail,
            originalFile: file
          });
        } else if (isCodeFile) {
          // Handle code files as text
          const reader = new FileReader();
          const text = await new Promise((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsText(file);
          });
          processedFiles.push({
            type: 'code',
            content: text,
            text: text,
            name: file.name,
            language: fileExtension
          });
        }
      }

      if (processedFiles.length > 0) {
        setUploadedFileData(processedFiles);
      } else {
        setUploadedFileData(null);
        if (onAttachmentChange) onAttachmentChange(false);
      }

      setIsProcessingFile(false);
      setResetFileUpload(false);
    } catch (error) {
      console.error('Error processing file:', error);
      toast?.showToast('Error processing file', 'error');
      setIsProcessingFile(false);
      if (onAttachmentChange) onAttachmentChange(false);
    }
  }, [uploadedFileData, onAttachmentChange, toast]);

  const clearUploadedFile = useCallback(() => {
    setUploadedFileData(null);
    setResetFileUpload(prev => !prev);
    if (onAttachmentChange) {
      onAttachmentChange(false);
    }
  }, [onAttachmentChange]);

  const removeFileByIndex = useCallback((index) => {
    if (!uploadedFileData) return;

    const filesArray = Array.isArray(uploadedFileData) ? uploadedFileData : [uploadedFileData];
    const newFiles = filesArray.filter((_, i) => i !== index);

    if (newFiles.length === 0) {
      clearUploadedFile();
    } else {
      setUploadedFileData(newFiles);
    }
  }, [uploadedFileData, clearUploadedFile]);

  const handleModelChange = useCallback((modelId) => {
    if (modelId !== DEEP_RESEARCH_MODEL_ID) {
      deepResearchChipLockRef.current = false;
    }
    setSelectedModel(modelId);
    if (availableModels) {
      localStorage.setItem('selectedModel', modelId);
      if (onModelChange && typeof onModelChange === 'function') {
        onModelChange(modelId);
      }
    }
  }, [availableModels, onModelChange]);

  const handleFilePreview = useCallback((file, index) => {
    if (!file) return;
    setFileToView({ ...file, __fileIndex: index });
    setIsFileViewerOpen(true);
  }, []);

  const handleFileSave = useCallback((updatedFile) => {
    if (!updatedFile || typeof updatedFile.__fileIndex !== 'number') return;
    setUploadedFileData(prev => {
      if (!prev) return prev;
      const filesArray = Array.isArray(prev) ? [...prev] : [prev];
      if (!filesArray[updatedFile.__fileIndex]) return prev;
      const { __fileIndex, ...cleanFile } = updatedFile;
      filesArray[updatedFile.__fileIndex] = cleanFile;
      return filesArray;
    });
    setFileToView(prev => (prev ? { ...updatedFile } : prev));
  }, []);

  const handleLiveModeToggle = useCallback((isOpen) => {
    setIsLiveModeOpen(isOpen);
  }, []);

  const handleCloseLiveMode = useCallback(() => {
    setIsLiveModeOpen(false);
  }, []);

  const handleStartEditing = useCallback(() => {
    setIsEditingTitle(true);
    setEditedTitle(chat?.title || newConversationLabel);
  }, [chat?.title, newConversationLabel]);

  const handleTitleChange = useCallback((e) => {
    setEditedTitle(e.target.value);
  }, []);

  const handleTitleSave = useCallback(() => {
    if (chat && updateChatTitle) {
      updateChatTitle(chat.id, editedTitle);
    }
    setIsEditingTitle(false);
  }, [chat, updateChatTitle, editedTitle]);

  const handleTitleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false);
      setEditedTitle(chat?.title || newConversationLabel);
    }
  }, [handleTitleSave, chat?.title, newConversationLabel]);

  // Message sender hook
  const { isLoading, submitMessage: sendChatMessage } = useMessageSender({
    chat,
    selectedModel,
    settings,
    availableModels,
    projects,
    addMessage,
    updateMessage,
    updateChatTitle,
    toastContext: toast,
    scrollToBottom,
    setUploadedFileData,
    setResetFileUpload,
    onAttachmentChange,
  });

  // Handle pending message from project detail page (pre-fill input)
  useEffect(() => {
    if (pendingMessage && chatInputAreaRef.current?.appendToInput) {
      chatInputAreaRef.current.appendToInput(pendingMessage);
      onPendingMessageConsumed?.();
    }
  }, [pendingMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effects
  useEffect(() => {
    if (shouldStartAnimationThisRender) {
      setAnimateDown(true);
      const timer = setTimeout(() => {
        setAnimateDown(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shouldStartAnimationThisRender]);

  useEffect(() => {
    if (!isEditingTitle) {
      setEditedTitle(chat?.title || newConversationLabel);
    }
  }, [chat, newConversationLabel, isEditingTitle]);

  // Fetch available image models from backend
  useEffect(() => {
    const fetchImageModels = async () => {
      try {
        const data = await listImageModelsApi();
        const models = data.models || [];
        setAvailableImageModels(models);

        const defaultModel = models.find(m => m.isDefault) || models[0];
        if (defaultModel && !selectedImageModel) {
          setSelectedImageModel(defaultModel);
        }
      } catch (error) {
        console.error('Failed to fetch image models:', error);
      }
    };
    fetchImageModels();
  }, []);

  useEffect(() => {
    prevIsEmptyRef.current = chatIsEmpty;
  }, [chatIsEmpty]);

  useEffect(() => {
    if (chatIsEmpty) {
      setAnimateDown(false);
    }
  }, [chatIsEmpty]);

  useEffect(() => {
    // Initial scroll and scroll on new messages
    scrollToBottom();
  }, [chat?.messages, scrollToBottom]);

  useEffect(() => {
    if (initialSelectedModel && initialSelectedModel !== selectedModel) {
      setSelectedModel(initialSelectedModel);
    }
  }, [initialSelectedModel, selectedModel]);

  useEffect(() => {
    if (typeof onFileViewerOpenChange === 'function') {
      onFileViewerOpenChange(isFileViewerOpen);
    }

    return () => {
      if (typeof onFileViewerOpenChange === 'function') {
        onFileViewerOpenChange(false);
      }
    };
  }, [isFileViewerOpen, onFileViewerOpenChange]);

  useEffect(() => {
    if (selectedModel && selectedModel !== DEEP_RESEARCH_MODEL_ID) {
      previousNonDeepResearchModelRef.current = selectedModel;
    }
  }, [selectedModel]);

  useEffect(() => {
    if (activeActionChip === 'deep-research') {
      deepResearchChipLockRef.current = true;
      if (selectedModel !== DEEP_RESEARCH_MODEL_ID) {
        handleModelChange(DEEP_RESEARCH_MODEL_ID);
      }
      return;
    }

    if (!deepResearchChipLockRef.current) {
      return;
    }

    deepResearchChipLockRef.current = false;

    if (selectedModel !== DEEP_RESEARCH_MODEL_ID) {
      return;
    }

    const nonDeepResearchModels = (availableModels || []).filter((model) => model?.id && model.id !== DEEP_RESEARCH_MODEL_ID);
    const fallbackModel =
      previousNonDeepResearchModelRef.current && previousNonDeepResearchModelRef.current !== DEEP_RESEARCH_MODEL_ID
        ? previousNonDeepResearchModelRef.current
        : getPreferredModelId(nonDeepResearchModels, DEFAULT_CHAT_MODEL_ID);

    if (fallbackModel && fallbackModel !== selectedModel) {
      handleModelChange(fallbackModel);
    }
  }, [activeActionChip, availableModels, handleModelChange, selectedModel]);

  useEffect(() => {
    if (chat?.id && initialSelectedModel && $sidebarCollapsed) {
      setSelectedModel(initialSelectedModel);
      setResetFileUpload(false);
    }
  }, [chat?.id, initialSelectedModel, $sidebarCollapsed]);

  useEffect(() => {
    if (onAttachmentChange) {
      onAttachmentChange(!!uploadedFileData);
    }
  }, [uploadedFileData, onAttachmentChange]);

  // Imperative handle
  useImperativeHandle(ref, () => ({
    handleFileSelected,
    appendToInput: (text) => {
      if (chatInputAreaRef.current && chatInputAreaRef.current.appendToInput) {
        chatInputAreaRef.current.appendToInput(text);
      }
    }
  }), [handleFileSelected]);

  // Early return for no chat - after all hooks
  if (!chat) {
    return (
      <ChatWindowContainer
        fontSize={settings?.fontSize}
        $sidebarCollapsed={$sidebarCollapsed}
        $focusModeActive={focusModeActive}
      >
        <EmptyState $isExiting={animateEmptyStateOut} $focusModeActive={focusModeActive}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <h3>{t('chat.emptyHeading')}</h3>
            <p>{t('chat.emptySubheading')}</p>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: '10px',
                padding: '8px 16px',
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {t('chat.emptyRefresh')}
            </button>
          </div>
        </EmptyState>
        <ChatInputArea
          chatIsEmpty={true}
          animateDown={false}
          $sidebarCollapsed={$sidebarCollapsed}
          settings={settings}
          onSubmitMessage={() => { }} // No-op for disabled state
          onFileSelected={() => { }}   // No-op
          isLoading={true} // Display as loading/disabled
          isProcessingFile={false}
          uploadedFile={null}
          onClearAttachment={() => { }}
          resetFileUploadTrigger={false}
          availableModels={availableModels}
          onMessageSent={onMessageSent}
        />
      </ChatWindowContainer>
    );
  }

  return (
    <ChatWindowContainer
      fontSize={settings?.fontSize}
      $sidebarCollapsed={$sidebarCollapsed}
      $focusModeActive={focusModeActive}
    >
      <ChatHeader
        style={{ opacity: ($sidebarCollapsed && isFocused) ? '0' : '1', transition: 'opacity 0.3s ease' }}
        $sidebarCollapsed={$sidebarCollapsed}
        $focusModeActive={focusModeActive}
      >
        <ChatTitleSection $sidebarCollapsed={$sidebarCollapsed}>
          <ModelSelectorsRow>
            {selectedModel !== 'instant' && !isImagePromptMode && activeActionChip !== 'deep-research' && (
              <ModelSelector
                selectedModel={selectedModel}
                models={availableModels.filter(m => m?.id !== DEEP_RESEARCH_MODEL_ID)}
                onChange={handleModelChange}
                key="model-selector"
                theme={theme}
              />
            )}
            {chat?.projectId && (() => {
              const proj = (projects || []).find(p => p.id === chat.projectId);
              return proj ? (
                <ProjectBadge to={`/projects/${proj.id}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  </svg>
                  {proj.projectName}
                </ProjectBadge>
              ) : null;
            })()}
            <ImageModelSelector
              availableModels={availableImageModels}
              selectedModel={selectedImageModel}
              onSelectModel={setSelectedImageModel}
              isVisible={isImagePromptMode}
              theme={theme}
            />
          </ModelSelectorsRow>
        </ChatTitleSection>
      </ChatHeader>

      {(showGreeting && (showEmptyStateStatic || animateEmptyStateOut)) && !isLiveModeOpen && (
        <EmptyState $isExiting={animateEmptyStateOut} $focusModeActive={focusModeActive}>
          {/* Content for empty state, e.g., a greeting message */}
        </EmptyState>
      )}

      {/* Main Chat Content */}
      <MessageList $focusModeActive={focusModeActive}>
        {!chatIsEmpty && !isLiveModeOpen && chat && Array.isArray(chat.messages) && chat.messages.map(message => (
          <ChatMessage
            key={message.id}
            message={message}
            chat={chat}
            showModelIcons={settings.showModelIcons}
            settings={settings}
            theme={theme}
            userProfilePicture={showProfilePicture ? profilePicture : null}
            showProfileIcon={showProfilePicture}
          />
        ))}
        <div ref={messagesEndRef} />
      </MessageList>

      {/* Live Mode Overlay - Now sibling to MessageList */}
      {isLiveModeOpen && (
        <LiveModeUI
          selectedModel={selectedModel}
          availableModels={availableModels}
          onModelChange={handleModelChange}
          onClose={handleCloseLiveMode}
        />
      )}

      {!isLiveModeOpen && (
        <ChatInputArea
          ref={chatInputAreaRef}
          chatIsEmpty={chatIsEmpty}
          animateDown={effectiveAnimateDownSignal}
          $sidebarCollapsed={$sidebarCollapsed}
          settings={settings}
          onSubmitMessage={sendChatMessage}
          onFileSelected={handleFileSelected}
          isLoading={isLoading}
          isProcessingFile={isProcessingFile}
          uploadedFile={uploadedFileData}
          onClearAttachment={clearUploadedFile}
          onRemoveFile={removeFileByIndex}
          resetFileUploadTrigger={resetFileUpload}
          availableModels={availableModels}
          currentModel={selectedModel}
          modelCapabilities={availableModels?.find(m => m.id === selectedModel)?.capabilities || {}}
          isWhiteboardOpen={isWhiteboardOpen}
          onToggleWhiteboard={onToggleWhiteboard}
          onCloseWhiteboard={onCloseWhiteboard}
          isEquationEditorOpen={isEquationEditorOpen}
          onToggleEquationEditor={onToggleEquationEditor}
          onCloseEquationEditor={onCloseEquationEditor}
          isGraphingOpen={isGraphingOpen}
          onToggleGraphing={onToggleGraphing}
          onCloseGraphing={onCloseGraphing}
          isFlowchartOpen={isFlowchartOpen}
          onToggleFlowchart={onToggleFlowchart}
          onCloseFlowchart={onCloseFlowchart}
          isSandbox3DOpen={isSandbox3DOpen}
          onToggleSandbox3D={onToggleSandbox3D}
          onCloseSandbox3D={onCloseSandbox3D}
          onToolbarToggle={onToolbarToggle}
          onLiveModeToggle={handleLiveModeToggle}
          isImagePromptMode={isImagePromptMode}
          onImageModeChange={setIsImagePromptMode}
          selectedImageModel={selectedImageModel}
          onActionChipChange={setActiveActionChip}
          onFilePreview={handleFilePreview}
          onUserTyping={onUserTyping}
          onMessageSent={onMessageSent}
        />
      )}

      <FileViewerModal
        isOpen={isFileViewerOpen}
        onClose={() => {
          setIsFileViewerOpen(false);
          setFileToView(null);
        }}
        file={fileToView}
        onSave={handleFileSave}
      />
    </ChatWindowContainer>
  );
});

ChatWindow.displayName = 'ChatWindow';

export default ChatWindow;
